import * as React from 'react'

import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { Loader } from '~/client/src/shared/components/Loader'
import PdfDocument from '~/client/src/shared/components/PdfDocument/PdfDocument'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import StruxhubSelect from '~/client/src/shared/components/StruxhubInputs/StruxhubSelect'

const selectPdfPage = 'Select PDF page'
const submit = 'Submit'
const page = 'Page'
const pageOf = (currPage: number, numOfAllPages: number) =>
  `${currPage} OF ${numOfAllPages}`

interface IProps {
  pdfFile: File
  onDialogSubmit: (selectedPage: number) => void
}

@observer
export default class PdfPageSelectorDialog extends React.Component<IProps> {
  @observable private pagesCount: number
  @observable private currentPageNumber: number = 1
  @observable private isLoading: boolean = true

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public render() {
    const { pdfFile } = this.props

    return (
      <Dialog open={true}>
        <DialogContent
          showCloseButton={false}
          className="tw:flex tw:max-h-[85vh] tw:w-[60%] tw:max-w-none tw:flex-col tw:sm:max-w-none"
        >
          <DialogHeader>
            <DialogTitle>{selectPdfPage}</DialogTitle>
          </DialogHeader>
          <div className="tw:relative tw:min-h-0 tw:overflow-auto">
            {this.isLoading && this.loaderElement}
            <PdfDocument
              file={pdfFile}
              className="tw:!flex tw:flex-row tw:justify-center tw:[&_canvas]:!h-full tw:[&_canvas]:!w-full"
              pageClassName={classList({
                'pdf-page-view': true,
                hidden: this.isLoading,
              })}
              loaderSize="md"
              pageNumber={this.currentPageNumber}
              onLoadSuccess={this.onDocumentLoadSuccess}
              onPageRenderSuccess={this.setLoadingDone}
            />
          </div>
          <DialogFooter>
            <div className="col">
              <div className="row x-center y-center py5">
                <div className="mw300">
                  <StruxhubSelect
                    label={page}
                    isDisabled={this.isLoading}
                    isRequired={true}
                    isRequiredTextHidden={true}
                    value={this.currentPageNumber.toString()}
                    onChange={this.onSelectPage}
                  >
                    {this.allPages.map(page => (
                      <option key={page} value={page} className="text large">
                        {pageOf(page, this.pagesCount)}
                      </option>
                    ))}
                  </StruxhubSelect>
                </div>
              </div>
              <div className="row x-center y-center py5 no-flex-children">
                <div
                  className={classList({
                    'pointer text extra-large primary-blue bold px8 py6 brada4 submit-btn': true,
                    'inactive-element': this.isLoading,
                  })}
                  onClick={this.onDialogSubmit}
                >
                  {submit}
                </div>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  private get loaderElement(): JSX.Element {
    return (
      <div className="relative row x-center y-center full-height full-width pa30">
        <Loader size="md" />
      </div>
    )
  }

  @action.bound
  private setLoadingActive() {
    this.isLoading = true
  }

  @action.bound
  private setLoadingDone() {
    this.isLoading = false
  }

  @action.bound
  private onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    this.pagesCount = numPages

    this.setCurrentPageNumber(1)
  }

  @action.bound
  private setCurrentPageNumber(pageNumber: number) {
    this.currentPageNumber = pageNumber
  }

  @action.bound
  private onSelectPage(event: any) {
    this.setLoadingActive()

    this.setCurrentPageNumber(Number(event?.target?.value))
  }

  @action.bound
  private onDialogSubmit() {
    this.props.onDialogSubmit(this.currentPageNumber)
  }

  private get allPages(): number[] {
    return [...Array(this.pagesCount).keys()]?.map(p => ++p)
  }
}
